import { graphql, readFragment, useQuery } from "@opensea/graphql"
import { useDateTimeFormatter } from "@opensea/i18n"
import {
  EmptyState,
  FlexCenter,
  FlexColumn,
  TextBody,
  TextOverflowTooltip,
} from "@opensea/ui-kit"
import { useClickAnyWhere } from "@opensea/ui-kit/hooks"
import { useTranslations } from "next-intl"
import { useRef } from "react"
import {
  ItemAvatar,
  ItemAvatarFragment,
} from "@/components/items/avatar/ItemAvatar"
import { useSetItemQuickView } from "@/components/items/item-page/ItemViewModal/use-set-item-quick-view"
import { useSetItemQuickViewFragment } from "@/components/items/item-page/ItemViewModal/use-set-item-quick-view.fragment"
import { itemUrlFragment } from "@/components/items/utils"
import { NativePriceFragment } from "@/components/price/NativePrice.fragment"
import { TokenPrice } from "@/components/price/TokenPrice"
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment"
import { Volume } from "@/components/price/Volume"
import { VolumeFragment } from "@/components/price/Volume.fragment"
import { withSuspense } from "@/components/suspense/WithSuspense"
import type { SalesChartData, SalesChartVolumeData } from "./SalesChart"
import { SalesChartTooltipSkeleton } from "./SalesChartTooltipSkeleton"

export const SalesChartScatterTooltipFragment = graphql(`
  fragment SalesChartScatterTooltip on ChartSaleV2 {
    saleKey
  }
`)

export const SalesChartScatterTooltipVolumeFragment = graphql(
  `
    fragment SalesChartScatterTooltipVolume on ChartVolume {
      volume {
        ...Volume
      }
    }
  `,
  [VolumeFragment],
)

export type RenderTooltipProps = {
  item: SalesChartData
  matchedColumnData?: SalesChartVolumeData
  series: "scatter" | "line"
  onItemClicked?: () => void
}

const query = graphql(
  `
    query SalesChartTooltipQuery($saleKey: String!) {
      itemChartDataBySaleKey(saleKey: $saleKey) {
        item {
          __typename
          ... on Item {
            imageUrl
            name
            ...useSetItemQuickView
          }
          chain {
            identifier
          }
          contractAddress
          tokenId
          ...ItemAvatar
          ...itemUrl
        }
        sale {
          time
          price {
            usd
            ...TokenPrice
            ...NativePrice
          }
        }
      }
    }
  `,
  [
    ItemAvatarFragment,
    TokenPriceFragment,
    NativePriceFragment,
    itemUrlFragment,
    useSetItemQuickViewFragment,
  ],
)

export const SalesChartScatterTooltip = withSuspense(
  ({
    matchedColumnData: volumeKey,
    item: itemKey,
    onItemClicked,
  }: RenderTooltipProps) => {
    const { saleKey } = readFragment(SalesChartScatterTooltipFragment, itemKey)
    const hasClicked = useRef(false)
    const chartVolume = readFragment(
      SalesChartScatterTooltipVolumeFragment,
      volumeKey,
    )
    const dateFormat = useDateTimeFormatter()
    const setItem = useSetItemQuickView()
    const t = useTranslations("SalesChartScatterTooltip")

    const [{ data }] = useQuery({
      query,
      variables: {
        saleKey,
      },
    })

    const item = data?.itemChartDataBySaleKey.item

    useClickAnyWhere(() => {
      // This should only ever happen once but the Tooltip can sometimes stay rendered
      if (item && !hasClicked.current) {
        hasClicked.current = true
        onItemClicked?.()
        setItem(item)
      }
    })

    if (!item) {
      return null
    }
    const price = data.itemChartDataBySaleKey.sale.price
    const time = data.itemChartDataBySaleKey.sale.time

    return (
      <FlexColumn className="w-[150px] cursor-pointer gap-2">
        <ItemAvatar className="mx-auto rounded" item={item} size={150} />
        <TextOverflowTooltip>
          <TextBody>{item.name}</TextBody>
        </TextOverflowTooltip>
        <FlexColumn className="gap-1">
          <FlexCenter className="flex-nowrap">
            <TextBody>
              {t.rich("price", {
                price: () => (
                  <TokenPrice conversion="historical" price={price} />
                ),
              })}
            </TextBody>
          </FlexCenter>
          <TextBody>
            {t.rich("vol", {
              vol: () =>
                chartVolume ? (
                  <Volume volume={chartVolume.volume} />
                ) : (
                  <TokenPrice conversion="historical" price={price} />
                ),
            })}
          </TextBody>
        </FlexColumn>
        <TextBody>
          <div>
            {dateFormat(new Date(time), { display: "datetime-numeric" })}
          </div>
        </TextBody>
      </FlexColumn>
    )
  },
  {
    fallback: <SalesChartTooltipSkeleton />,
    errorFallback: <EmptyState size="lg" variant="no-data" />,
  },
)
